import { useState } from 'react';
import { TablePaginationConfig } from 'antd';
import { SorterResult } from 'antd/lib/table/interface';
import { useParams } from 'react-router-dom';
import { NumberParam, StringParam, useQueryParams, withDefault } from 'use-query-params';
import { CategoryOrderByParams, OrderDirectionParam, useDebounce } from '@app/utils';
import { useQuestionCategories } from '@data/hooks';
import {
  CategoryOrderBy,
  CategoryType,
  ListQuestionCategoryView,
  QuestionCategoryView,
  QuestionsApiGetQuestionCategoriesRequest,
} from '@data/sdk/match';
import { OrderDirection } from '@data/utils/enums';

export const useQuestionClassification = (
  categoryType: CategoryType,
  isMaxLimit: boolean = false,
) => {
  const { questionId = '' } = useParams<{ questionId: string }>();
  const [isCreateCategoryOpen, setIsCreateCategoryOpen] = useState(false);

  const [query, setQuery] = useQueryParams({
    limit: withDefault(NumberParam, isMaxLimit ? 100 : 10),
    page: withDefault(NumberParam, 1),
    searchString: withDefault(StringParam, ''),
    orderBy: withDefault(CategoryOrderByParams, CategoryOrderBy.TrialCount),
    orderDirection: withDefault(OrderDirectionParam, OrderDirection.DESC),
  });
  const { limit, page, orderBy, orderDirection, searchString } = query;

  const debouncedSearch = useDebounce(searchString);

  const params: QuestionsApiGetQuestionCategoriesRequest = {
    limit,
    offset: (page - 1) * limit,
    id: questionId,
    categoryType,
    searchString: debouncedSearch || undefined,
    ...(categoryType === 'icdCode' && { orderBy, orderDirection }),
  };

  const { data: categories, isLoading: isQuestionCategoriesLoading } =
    useQuestionCategories(params);

  const openCreateCategory = () => {
    setIsCreateCategoryOpen(true);
  };

  const closeCreateCategory = () => {
    setIsCreateCategoryOpen(false);
  };

  const handleTableChange = (
    { pageSize, current }: TablePaginationConfig,
    sorter: SorterResult<QuestionCategoryView> | SorterResult<QuestionCategoryView>[],
  ) => {
    setQuery({
      page: pageSize && pageSize > limit ? 1 : current,
      limit: pageSize,
      orderDirection:
        (sorter as SorterResult<ListQuestionCategoryView>).order === 'descend'
          ? OrderDirection.DESC
          : OrderDirection.ASC,
      orderBy:
        ((sorter as SorterResult<ListQuestionCategoryView>).field as CategoryOrderBy) ?? orderBy,
    });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery({ searchString: e.target.value || undefined, page: 1 });
  };

  return {
    questionId,
    categories,
    searchString,
    current: page,
    pageSize: limit,
    isCreateCategoryOpen,
    isLoading: isQuestionCategoriesLoading,
    openCreateCategory,
    closeCreateCategory,
    handleTableChange,
    handleSearchChange,
  };
};
